import { xIsSet, xIsNull, xIsUndefined, xIsString, xIsNumber, xIsInteger, xIsBoolean, xIsObject, xIsArray, xIsSpecificClass, xIsFunction, xIsAnySpecificClass, xIsObjectEmpty } from './XTypeChecks.js';
import { xClearObject, xDeepCopy, xDeepMerge, xMerge, xRandomString, xSetHiddenField, xStringify } from './XUtil.js';
import { XTypeError } from './XError.js';

export function xExtendClassForAttachments(Class)
{
    if(!('_attachments' in Class.prototype))
    {
        Object.defineProperty(Class.prototype, '_attachments', {
            get: function()
            {
                return xIsSpecificClass(this._attachmentData, Set) ? Array.from(this._attachmentData) : (xIsSet(this._attachmentData) ? [this._attachmentData] : []);
            },
            enumerable: false
        });
    }
    if(!('_addAttachment' in Class.prototype))
    {
        Object.defineProperty(Class.prototype, '_addAttachment', {
            value: function(attachment, ...attachmentAddedParameters)
            {
                if(xIsSpecificClass(this._attachmentData, Set))
                {
                    this._attachmentData.add(attachment);
                }
                else if(xIsSet(this._attachmentData) && this._attachmentData != attachment)
                {
                    xSetHiddenField(this, '_attachmentData', new Set([this._attachmentData, attachment]));
                }
                else
                {
                    xSetHiddenField(this, '_attachmentData', attachment);
                }
                if('_attachmentAdded' in this)
                {
                    this._attachmentAdded(attachment, ...attachmentAddedParameters);
                }
            },
            enumerable: false
        });
    }
    if(!('_removeAttachment' in Class.prototype))
    {
        Object.defineProperty(Class.prototype, '_removeAttachment', {
            value: function(attachment, ...attachmentRemovedParameters)
            {
                if(xIsSpecificClass(this._attachmentData, Set))
                {
                    this._attachmentData.delete(attachment);
                    if(this._attachmentData.size == 1)
                    {
                        xSetHiddenField(this, '_attachmentData', this._attachmentData.values().next().value);
                    }
                }
                else if(this._attachmentData == attachment)
                {
                    delete this._attachmentData;
                }
                if('_attachmentRemoved' in this)
                {
                    this._attachmentRemoved(attachment, ...attachmentRemovedParameters);
                }
            },
            enumerable: false
        });
    }
    if(!('_callAttachments' in Class.prototype))
    {
        Object.defineProperty(Class.prototype, '_callAttachments', {
            value: function(functionName, ...parameters)
            {
                for(const attachment of this._attachments)
                {
                    if(functionName in attachment)
                    {
                        attachment[functionName](...parameters);
                    }
                }
            },
            enumerable: false
        });
    }
    if(!('_destroyAttachments' in Class.prototype))
    {
        Object.defineProperty(Class.prototype, '_destroyAttachments', {
            value: function(...parameters)
            {
                const attachments = Array.from(this._attachments);
                delete this._attachmentData;
                for(const attachment of attachments)
                {
                    attachment._destroy?.(...parameters);
                }
            },
            enumerable: false
        });
    }
}

export class XType 
{
    clone(storage)
    {
        return new this.constructor(this.toJSON());
    }

    toJSON()
    {
        return this.valueOf();
    }

    _toLocalizedJSON(locale)
    {
        if(this._localized)
        {
            const object = {};
            object[locale] = this.valueOf();
            return object;
        }
        return this.valueOf();
    }

    _extractLocalizationFromData(data, locale, localeFallback, keepStructure=false)
    {
        const isObject = xIsObject(data);
        const value = isObject ? (locale in data ? data[locale] : (localeFallback in data ? data[localeFallback] : this.valueOf())) : data;
        return keepStructure && isObject ? {[locale]:value} : value;
    }
    
    _accessLocalizationsInData(data, location=[])
    {
        return this._localized && xIsObject(data) ? [{location: location, data: data, typedData: this}] : [];
    }

    _mergeLocalizationIntoData(data, locale, localeFallback)
    {
        if(this._localized)
        {
            if(!xIsObject(data))
            {
                const object = {};
                object[localeFallback] = data;
                data = object;
            }
            data[locale] = this.valueOf();
            return data;
        }
        return this.valueOf();
    }

    _markAsLocalizedByData(data)
    {
        if(this._localized)
        {
            return;
        }
        if(xIsObject(data))
        {
            xSetHiddenField(this, '_localized', true);
        }
        else
        {
            delete this._localized;
        }
    }

    _markAsLocalized(localized=true)
    {
        if(localized)
        {
            xSetHiddenField(this, '_localized', true);
        }
        else
        {
            delete this._localized;
        }
    }

    _destroy()
    {
        this._destroyAttachments(this);
    }

    _updateParenting(parent)
    {
        if(!xIsUndefined(parent._parent))
        {
            this._enableParenting(parent);
        }
    }

    _enableParenting(parent)
    {
        this._parent = parent;
    }

    _tagChildren()
    {
        this._tagged = true;
    }

    _untagChildren()
    {
        delete this._tagged;
    }

    _childChanged(sender, oldValue)
    {
        this._callAttachments('_changed', sender, oldValue);
        if(this._tagged)
        {
            return;
        }
        if(this._parent)
        {
            this._parent._childChanged(sender, oldValue);
        }
    }
}
xExtendClassForAttachments(XType);

export class XPrimitive extends XType 
{
    constructor(localized = false)
    {
        super();
        if(localized)
        {
            this._markAsLocalized(localized);
        }
    }

    clone(storage)
    {
        const primitive = new this.constructor(this.toJSON(), {
            localized: this._localized
        });
        return primitive;
    }
}

export class XString extends XPrimitive 
{
    #value = '';

    constructor(value = '', {
        localized = false
    } = {})
    {
        super(localized);
        this._value = value;
    }

    set _value(value)
    {
        if(!xIsString(value))
        {
            throw new XTypeError('Value needs to be a native JavaScript string!', this, value);
        }
        const oldValue = this.#value;
        this.#value = value;
        this._callAttachments('_setValue', this, oldValue);
        this._childChanged(this, oldValue);
    }

    get _value()
    {
        return this.#value;
    }

    toString()
    {
        return this.#value;
    }

    valueOf()
    {
        return this.#value;
    }
}
export function xString(value, {
    localized = false
} = {})
{
    return new XString(value, {
        localized: localized
    });
}

export class XInteger extends XPrimitive 
{
    #value = 0;

    constructor(value = 0, {
        localized = false
    } = {})
    {
        super(localized);
        this._value = value;
    }

    set _value(value)
    {
        if(!xIsInteger(value))
        {
            throw new XTypeError('Value needs to be a native JavaScript integer!', this, value);
        }
        const oldValue = this.#value;
        this.#value = value;
        this._callAttachments('_setValue', this, oldValue);
        this._childChanged(this, oldValue);
    }

    get _value()
    {
        return this.#value;
    }

    toString()
    {
        return String(this.#value);
    }

    valueOf()
    {
        return this.#value;
    }
}
export function xInteger(value, {
    localized = false
} = {})
{
    return new XInteger(value, {
        localized: localized
    });
}

export class XNumber extends XPrimitive 
{
    #value = 0.0;

    constructor(value = 0.0, {
        localized = false
    } = {})
    {
        super(localized);
        this._value = value;
    }

    set _value(value)
    {
        if(!xIsNumber(value))
        {
            throw new XTypeError('Value needs to be a native JavaScript number!', this, value);
        }
        const oldValue = this.#value;
        this.#value = value;
        this._callAttachments('_setValue', this, oldValue);
        this._childChanged(this, oldValue);
    }

    get _value()
    {
        return this.#value;
    }

    toString()
    {
        return String(this.#value);
    }

    valueOf()
    {
        return this.#value;
    }
}
export function xNumber(value, {
    localized = false
} = {})
{
    return new XNumber(value, {
        localized: localized
    });
}

export class XBoolean extends XPrimitive 
{
    #value = false;

    constructor(value = false, {
        localized = false
    } = {})
    {
        super(localized);
        this._value = value;
    }

    set _value(value)
    {
        if(!xIsBoolean(value))
        {
            throw new XTypeError('Value needs to be a native JavaScript boolean!', this, value);
        }
        const oldValue = this.#value;
        this.#value = value;
        this._callAttachments('_setValue', this, oldValue);
        this._childChanged(this, oldValue);
    }

    get _value()
    {
        return this.#value;
    }

    toString()
    {
        return String(this.#value);
    }

    valueOf()
    {
        return this.#value;
    }
}
export function xBoolean(value, {
    localized = false
} = {})
{
    return new XBoolean(value, {
        localized: localized
    });
}

export class XOptional extends XType 
{
    #value = null;
    #type = null;
    #storage = null;

    constructor(type, value = null, storage = null)
    {
        super();
        this.#storage = storage;
        if(!(type instanceof XType))
        {
            throw new XTypeError('Type needs to be a derived type of XType, but got "' + (typeof type) + '"!', this, type);
        }
        this.#type = type;
        this._value = value;
    }

    clone(storage)
    {
        return new XOptional(this.#type, this.toJSON(), storage ?? this.#storage);
    }

    _enableParenting(parent)
    {
        this._parent = !xIsUndefined(parent) ? parent : null;
        if(!xIsNull(this.#value))
        {
            this.#value._enableParenting(this);
        }
    }

    _tagChildren()
    {
        if(this._tagged)
        {
            return;
        }
        this._tagged = true;
        if(!xIsNull(this.#value))
        {
            this.#value._tagChildren();
        }
    }

    _untagChildren()
    {
        if(!this._tagged)
        {
            return;
        }
        delete this._tagged;
        if(!xIsNull(this.#value))
        {
            this.#value._untagChildren();
        }
    }

    set _value(value)
    {
        const oldValue = this.#value;
        if(xIsNull(value))
        {
            this.#value = null;
        }
        else
        {
            this.#value = value;
            if(this.#value.constructor != this.#type.constructor)
            {
                this.#value = this.#type.clone(this.#storage);
                this.#value._tagChildren();
                this.#value._value = value;
                this.#value._untagChildren();
            }
            this.#value._updateParenting(this);
        }
        this._callAttachments('_setValue', this, oldValue);
        this._childChanged(this, oldValue);
    }

    get _value()
    {
        return this.#value;
    }

    toString()
    {
        return xIsNull(this.#value) ? 'null' : this.#value.toString();
    }

    valueOf()
    {
        return xIsNull(this.#value) ? null : this.#value.valueOf();
    }

    toJSON()
    {
        return xIsNull(this.#value) ? null : this.#value.toJSON();
    }

    _toLocalizedJSON(locale)
    {
        return xIsNull(this.#value) ? null : this.#value._toLocalizedJSON(locale);
    }

    _extractLocalizationFromData(data, locale, localeFallback, keepStructure=false)
    {
        return xIsNull(data) ? null : this.#type._extractLocalizationFromData(data, locale, localeFallback, keepStructure);
    }
    
    _accessLocalizationsInData(data, location=[])
    {
        return xIsNull(this.#value) ? [] : this.#value._accessLocalizationsInData(data, location);
    }

    _mergeLocalizationIntoData(data, locale, localeFallback)
    {
        return xIsNull(this.#value) ? null : this.#value._mergeLocalizationIntoData(data, locale, localeFallback);
    }

    _markAsLocalizedByData(data)
    {
        this.#value?._markAsLocalizedByData(data);
    }

    _destroy()
    {
        super._destroy();
        if(!xIsNull(this.#value))
        {
            this.#value._destroy();
        }
    }
}
export function xOptional(type, value = null)
{
    return new XOptional(type, value);
}

export class XArray extends XType 
{
    #value = [];
    #type = null;
    #storage = null;

    constructor(type, value = [], storage = null)
    {
        super();
        this.#storage = storage;
        if(!(type instanceof XType))
        {
            throw new XTypeError('Type needs to be a derived type of XType, but got "' + (typeof type) + '"!', this, type);
        }
        this.#type = type;
        this._value = value;
    }

    clone(storage)
    {
        return new XArray(this.#type, this.toJSON(), storage ?? this.#storage);
    }

    push(...values)
    {
        const newItems = [];
        const firstValue = this.length == 0;
        for(const value of values)
        {
            let newItem = value;
            if(newItem.constructor != this.#type.constructor)
            {
                newItem = this.#type.clone(this.#storage);
                newItem._tagChildren();
                newItem._value = value;
                newItem._untagChildren();
            }
            newItem._updateParenting(this);
            newItems.push(newItem);
        }
        this._callAttachments('push', ...newItems);
        const result = this.#value.push(...newItems);
        if(firstValue)
        {
            this._callAttachments('_emptyStateChanged', false);
        }
        this._childChanged(this);
        return result;
    }

    pop()
    {
        this._callAttachments('pop');
        const lastValue = this.length == 1;
        /*
        if(this.#value.length > 0)
        {
            this.#value[this.#value.length-1]._destroy();
        }
        */
        this.#value.pop();
        if(lastValue)
        {
            this._callAttachments('_emptyStateChanged', true);
        }
        this._childChanged(this);
    }

    splice(start, deleteCount, ...values)
    {
        const oldLength = this.length; 
        deleteCount = Math.max(deleteCount, 0);
        start = Math.min(this.length, Math.max(-this.length, start));
        if(start < 0)
        {
            start = this.length + start;
        }
        const newItems = [];
        for(const value of values)
        {
            let newItem = value;
            if(newItem.constructor != this.#type.constructor)
            {
                newItem = this.#type.clone(this.#storage);
                newItem._tagChildren();
                newItem._value = value;
                newItem._untagChildren();
            }
            newItem._updateParenting(this);
            newItems.push(newItem);
        }
        this._callAttachments('splice', start, deleteCount, ...newItems);
        const end = Math.min(start + deleteCount, this.#value.length);
        /*
        for(let i = start; i < end; i++)
        {
            this.#value[i]._destroy();
        }
        */
        const result = this.#value.splice(start, deleteCount, ...newItems);
        const becomesEmpty = oldLength > 0 && this.length == 0;
        const becomesNonEmpty = oldLength == 0 && this.length > 0;
        if(becomesEmpty || becomesNonEmpty)
        {
            this._callAttachments('_emptyStateChanged', becomesEmpty);
        }
        this._childChanged(this);
        return result;
    }

    includes(searchElement, fromIndex)
    {
        fromIndex = xIsSet(fromIndex) ? fromIndex : 0;
        for(let i = fromIndex; i < this.#value.length; i++)
        {
            if(this.#value[i]._value == searchElement)
            {
                return true;
            }
        }
        return false;
    }

    _remove(indexOrElement)
    {
        const index = xIsInteger(indexOrElement) ? indexOrElement : this._value.indexOf(indexOrElement);
        if(index >= 0)
        {
            this.splice(index, 1);
            return true;
        }
        return false;
    }

    get length()
    {
        return this.#value.length;
    }

    _enableParenting(parent)
    {
        this._parent = !xIsUndefined(parent) ? parent : null;
        for(let item of this.#value)
        {
            item._enableParenting(this);
        }
    }

    _tagChildren()
    {
        if(this._tagged)
        {
            return;
        }
        this._tagged = true;
        for(let item of this.#value)
        {
            item._tagChildren();
        }
    }

    _untagChildren()
    {
        if(!this._tagged)
        {
            return;
        }
        delete this._tagged;
        for(let item of this.#value)
        {
            item._untagChildren();
        }
    }

    set _value(value)
    {
        if(!xIsArray(value))
        {
            throw new XTypeError('Value needs to be an array!', this, value);
        }
        const oldLength = this.#value.length;
        const newLength = value.length;
        const minLength = Math.min(newLength, oldLength);
        for(let i = 0; i < minLength; i++)
        {
            if(value[i].constructor == this.#type.constructor)
            {
                if(this.#value[i] != value[i])
                {
                    const oldValue = this.#value[i];
                    this.#value[i] = value[i];
                    this.#value[i]._updateParenting(this);
                    this._callAttachments('_referenceChanged', i, this.#value[i], oldValue);
                }
            }
            else
            {
                this.#value[i]._value = value[i];
            }
        }
        if(oldLength < newLength)
        {
            for(let i = oldLength; i < newLength; i++)
            {
                this.push(value[i]);
            }
        }
        else
        {
            for(let i = newLength; i < oldLength; i++)
            {
                this.pop();
            }
        }
    }

    get _value()
    {
        return this.#value;
    }

    get _type()
    {
        return this.#type;
    }

    toString()
    {
        return JSON.stringify(this.valueOf());
    }

    valueOf()
    {
        const data = [];
        for(const item of this.#value)
        {
            data.push(item.valueOf());
        }
        return data;
    }

    toJSON()
    {
        const data = [];
        for(const item of this.#value)
        {
            data.push(item.toJSON());
        }
        return data;
    }

    _toLocalizedJSON(locale)
    {
        const data = [];
        for(const item of this.#value)
        {
            data.push(item._toLocalizedJSON(locale));
        }
        return data;
    }

    _extractLocalizationFromData(data, locale, localeFallback, keepStructure=false)
    {
        const localizedData = [];
        for(const item of data)
        {
            localizedData.push(this.#type._extractLocalizationFromData(item, locale, localeFallback, keepStructure));
        }
        return localizedData;
    }

    _accessLocalizationsInData(data, location=[])
    {
        const localizedData = [];
        const minLength = Math.min(data.length, this.#value.length);
        for(let i = 0; i < minLength; i++)
        {
            localizedData.push(...this.#value[i]._accessLocalizationsInData(data[i], [...location, i]));
        }
        return localizedData;
    }

    _mergeLocalizationIntoData(data, locale, localeFallback)
    {
        const localizedData = [];
        const minLength = Math.min(data.length, this.#value.length);
        for(let i = 0; i < minLength; i++)
        {
            localizedData.push(this.#value[i]._mergeLocalizationIntoData(data[i], locale, localeFallback));
        }
        for(let i = minLength; i < this.#value.length; i++)
        {
            localizedData.push(this.#value[i]._toLocalizedJSON(locale));
        }
        return localizedData;
    }

    _markAsLocalizedByData(data)
    {
        const minLength = Math.min(data.length, this.#value.length);
        for(let i = 0; i < minLength; i++)
        {
            this.#value[i]._markAsLocalizedByData(data[i]);
        }
    }

    _destroy()
    {
        super._destroy();
        for(let item of this.#value)
        {
            item._destroy();
        }
    }
}
export function xArray(type, value = [])
{
    return new XArray(type, value);
}

export class XObject extends XType 
{
    #value = {};
    #typename = null;
    #storage = null;

    constructor(value = {}, typename = null, storage = null)
    {
        super();
        this.#storage = storage;
        if(!xIsObject(value))
        {
            throw new XTypeError('Value needs to be a native JavaScript object filled with fields that are derived of XType!', this, value);
        }
        for(let key in value)
        {
            if(key == '$')
            {
                typename = value[key];
                continue;
            }
            this._add(key, value[key]);
        }
        if(!(xIsString(typename) || xIsNull(typename)))
        {
            throw new XTypeError('If set, the typename of an object needs to be a native JavaScript string.', this, typename);
        }
        this.#typename = typename;
    }

    clone(storage)
    {
        storage = storage ?? this.#storage;
        let value = {};
        for(let key in this.#value)
        {
            value[key] = this.#value[key].clone(storage);
        }
        return new XObject(value, this.#typename, storage);
    }

    _shallowCopy(deep = false)
    {
        const object = new XObject();
        for(let key in this.#value)
        {
            const value = this.#value[key];
            if(deep)
            {
                object._add(key, xIsSpecificClass(value, XObject) ? value._shallowCopy(deep) : value);
            }
            else
            {
                object._add(key, value);
            }
        }
        return object;
    }

    _shallowMerge(extension)
    {
        const object = new XObject();
        for(let key in this.#value)
        {
            object._add(key, this.#value[key]);
        }
        for(let key in extension)
        {
            if(key in object)
            {
                continue;
            }
            object._add(key, extension[key]);
        }
        return object;
    }

    _enableParenting(parent)
    {
        this._parent = !xIsUndefined(parent) ? parent : null;
        for(let key in this.#value)
        {
            this.#value[key]._enableParenting(this);
        }
    }

    _tagChildren()
    {
        if(this._tagged)
        {
            return;
        }
        this._tagged = true;
        for(let key in this.#value)
        {
            this.#value[key]._tagChildren();
        }
    }

    _untagChildren()
    {
        if(!this._tagged)
        {
            return;
        }
        delete this._tagged;
        for(let key in this.#value)
        {
            this.#value[key]._untagChildren();
        }
    }

    #updateAccessors(key)
    {
        const item = this.#value[key];
        if(xIsSpecificClass(item, XObject))
        {
            Object.defineProperty(this, key, 
            {
                set(value) 
                {
                    this.#value[key]._value = value;
                },
                get() 
                {
                    return this.#value[key];
                }
            });
        }
        else if(xIsSpecificClass(item, XArray))
        {
            if(xIsAnySpecificClass(item._type, XArray, XObject))
            {
                Object.defineProperty(this, key, 
                {
                    set(value) 
                    {
                        this.#value[key]._value = value;
                    },
                    get() 
                    {
                        const handler = {
                            set: (target, propertyKey, receiver) =>
                            {
                                target[propertyKey]._value = receiver;
                            },
                            get: (target, propertyKey, receiver) =>
                            {
                                if(propertyKey in this.#value[key] && xIsFunction(this.#value[key][propertyKey])) 
                                {
                                    return (...parameters) => this.#value[key][propertyKey].call(this.#value[key], ...parameters);
                                }
                                return Reflect.get(target, propertyKey, receiver);
                            }
                        };
                        return new Proxy(this.#value[key]._value, handler);
                    }
                });
                Object.defineProperty(this, key + '_', 
                {
                    set(value) 
                    {
                        if(!xIsSpecificClass(value, XArray))
                        {
                            throw new XTypeError('If you change an object property, it has to be of the same type', this.#value[key], value);
                        }
                        this.#value[key] = value;
                    }
                });
            }
            else
            {
                Object.defineProperty(this, key, 
                {
                    set(value) 
                    {
                        this.#value[key]._value = value;
                    },
                    get() 
                    {
                        const handler = {
                            set: (target, propertyKey, receiver) =>
                            {
                                target[propertyKey]._value = receiver;
                            },
                            get: (target, propertyKey, receiver) =>
                            {
                                if(propertyKey in this.#value[key] && xIsFunction(this.#value[key][propertyKey])) 
                                {
                                    return (...parameters) => this.#value[key][propertyKey].call(this.#value[key], ...parameters);
                                }
                                else if(propertyKey == Symbol.iterator)
                                {
                                    return function* ()
                                    {
                                        for(let i = 0; i < target.length; i++)
                                        {
                                            yield target[i]._value;
                                        }
                                    }
                                }
                                else if(/^\d+$/.test(propertyKey)) 
                                {
                                    return target[propertyKey]._value;
                                }
                                return Reflect.get(target, propertyKey, receiver);
                            }
                        };
                        return new Proxy(this.#value[key]._value, handler);
                    }
                });
                Object.defineProperty(this, key + '_', 
                {
                    set(value) 
                    {
                        if(!xIsSpecificClass(value, XArray))
                        {
                            throw new XTypeError('If you change an object property, it has to be of the same type', this.#value[key], value);
                        }
                        this.#value[key] = value;
                    },
                    get() 
                    {
                        const handler = {
                            set: (target, propertyKey, receiver) =>
                            {
                                target[propertyKey]._value = receiver;
                            },
                            get: (target, propertyKey, receiver) =>
                            {
                                if(propertyKey in this.#value[key] && xIsFunction(this.#value[key][propertyKey])) 
                                {
                                    return (...parameters) => this.#value[key][propertyKey].call(this.#value[key], ...parameters);
                                }
                                return Reflect.get(target, propertyKey, receiver);
                            }
                        };
                        return new Proxy(this.#value[key]._value, handler);
                    }
                });
            }
        }
        else if(xIsSpecificClass(item, XOptional))
        {
            Object.defineProperty(this, key, 
            {
                set(value) 
                {
                    this.#value[key]._value = value;
                },
                get() 
                {
                    const value = this.#value[key]._value;
                    return xIsNull(value) ? null : value._value;
                }
            });
        }
        else
        {
            Object.defineProperty(this, key, 
            {
                set(value) 
                {
                    this.#value[key]._value = value;
                },
                get() 
                {
                    return this.#value[key]._value;
                },
            });
        }
        if(!xIsSpecificClass(item, XArray))
        {
            Object.defineProperty(this, key + '_', 
            {
                set(value) 
                {
                    if(this._value[key].constructor != value.constructor)
                    {
                        throw new XTypeError('If you change an object property, it has to be of the same type', this.#value[key], value);
                    }
                    this._value[key] = value;
                },
                get() 
                {
                    return this.#value[key];
                }
            });
        }
    }

    _add(key, item)
    {
        if(!(item instanceof XType))
        {
            throw new XTypeError('Value of field "' + key + '" needs to be a derived type of XType, but got "' + (typeof item) + '"!', this, item);
        }
        this.#value[key] = item;
        this.#updateAccessors(key);
    }

    set _value(value)
    {
        if(!xIsObject(value))
        {
            throw new XTypeError('Value needs to be a native JavaScript object!', this, value);
        }
        if('$' in value && value.$ !== this.#typename)
        {
            throw new XTypeError('Value has object type "' + value.$ + '", but needs to have type "' + this.#typename + '".', this);
        }
        for(let key in this.#value)
        {
            if(key in value)
            {
                const existingValue = this.#value[key];
                existingValue._tagChildren();
                existingValue._value = value[key];
                existingValue._untagChildren();
            }
        }
        this._childChanged(this);
    }

    get _value()
    {
        return this.#value;
    }

    get _typename()
    {
        return this.#typename;
    }

    get _hasRevisions()
    {
        return xIsSet(this._revisions);
    }

    get _isDraft()
    {
        return !this._hasRevisions || this._currentRevision === -1;
    }

    get _hasDraft()
    {
        return this._draft || this._isDraft;
    }

    get _isCurrentVersion()
    {
        return !this.#storage.isVersioned || this._version == this.#storage.version;
    }

    set _revision(revision)
    {
        if(!this._hasRevisions || revision == this._currentRevision)
        {
            return;
        }
        if(revision == -1)
        {
            if(this._draft)
            {
                this._value = this._draft;
                delete this._draft;
            }
            else if(!this._revisionDataObject.$draft)
            {
                this._revisionDataObject.$draft = xDeepCopy(this._revisions[this._currentRevision]);
            }
            this._currentRevision = -1;
        }
        else
        {
            const revisionString = String(revision);
            if(revisionString in this._revisions)
            {
                if(this._currentRevision == -1)
                {
                    xSetHiddenField(this, '_draft', this.toJSON());
                }
                this._value = this._extractLocalizationFromData(this._revisions[revisionString], this.#storage.locale, this.#storage.localeFallback);
                this._currentRevision = revision;
            }
        }
    }

    _setDraft()
    {
        this._revision = -1;
    }

    _setDraftInCurrentVersion()
    {
        if(this._isCurrentVersion)
        {
            this._setDraft();
            return;
        }
        this._updateStorageData();
        let thisDraft = this._revisionDataObject;
        if('$draft' in thisDraft)
        {
            thisDraft = thisDraft.$draft;
        }
        else if('$revisions' in thisDraft)
        {
            thisDraft = xDeepCopy(thisDraft.$revisions[this._revisionTimestamps[this._revisionTimestamps.length-1]]);
        }
        else
        {
            thisDraft = {...thisDraft};
        }
        xClearObject(this._revisionDataObject, '$revisions');
        const versionedRevisionDataObject = this.#storage.storage[this._key];
        const currentVersion = this.#storage.version;
        if(!(currentVersion in versionedRevisionDataObject))
        {
            versionedRevisionDataObject[currentVersion] = {};
        }
        const currentVersionRevisionDataObject = versionedRevisionDataObject[currentVersion];
        if('$revisions' in currentVersionRevisionDataObject)
        {
            currentVersionRevisionDataObject.$draft = thisDraft;
        }
        else
        {
            const draft = this._mergeLocalizationIntoData(thisDraft, this.#storage.locale, this.#storage.localeFallback);
            xClearObject(currentVersionRevisionDataObject);
            xMerge(currentVersionRevisionDataObject, draft);
        }
        if(xIsObjectEmpty(this._revisionDataObject))
        {
            delete versionedRevisionDataObject[this._version];
        }
        this._version = currentVersion;
        this._revisionDataObject = versionedRevisionDataObject[currentVersion];
        delete this._revisions;
        if('$revisions' in this._revisionDataObject)
        {
            this._revisions = this._revisionDataObject.$revisions;
            this._revisionTimestamps = [];
            for(let timestampString in this._revisions)
            {
                this._revisionTimestamps.push(parseInt(timestampString));
            }
            this._currentRevision = -1;
            this._revisionTimestamps = this._revisionTimestamps.sort((a,b)=>a-b);
        }
    }

    _publishDraft(timestamp)
    {
        if(!this._hasDraft)
        {
            return;
        }
        this._updateStorageData();
        let thisDraft = this._revisionDataObject;
        if('$draft' in thisDraft)
        {
            thisDraft = thisDraft.$draft;
        }
        else
        {
            thisDraft = {...thisDraft};
        }
        xClearObject(this._revisionDataObject, '$revisions');
        if(!this._hasRevisions)
        {
            this._revisionDataObject.$revisions = {};
            this._revisionTimestamps = [];
            this._revisions = this._revisionDataObject.$revisions;
        }
        timestamp = timestamp ?? Date.now();
        this._revisionTimestamps.push(timestamp);
        this._revisions[String(timestamp)] = thisDraft;
        this._currentRevision = timestamp;
        return timestamp;
    }

    toString()
    {
        return JSON.stringify(this.valueOf());
    }

    valueOf()
    {
        const data = {};
        if(xIsSet(this.#typename))
        {
            data.$ = this.#typename;
        }
        for(const key in this.#value)
        {
            data[key] = this.#value[key].valueOf();
        }
        return data;
    }

    toJSON()
    {
        const data = {};
        if(xIsSet(this.#typename))
        {
            data.$ = this.#typename;
        }
        for(const key in this.#value)
        {
            data[key] = this.#value[key].toJSON();
        }
        return data;
    }

    _toLocalizedJSON(locale)
    {
        locale = locale ?? this.#storage.locale;
        const data = {};
        for(const key in this.#value)
        {
            data[key] = this.#value[key]._toLocalizedJSON(locale);
        }
        return data;
    }

    _extractLocalizationFromData(data, locale, localeFallback, keepStructure=false)
    {
        const localizedData = {};
        for(const key in data)
        {
            if(key in this.#value)
            {
                localizedData[key] = this.#value[key]._extractLocalizationFromData(data[key], locale, localeFallback, keepStructure);
            }
        }
        return localizedData;
    }

    _accessLocalizationsInData(data, location=[])
    {
        const localizedData = [];
        for(const key in this.#value)
        {
            if(key in data)
            {
                localizedData.push(...this.#value[key]._accessLocalizationsInData(data[key], [...location, key]));
            }
        }
        return localizedData;
    }

    _mergeLocalizationIntoData(data, locale, localeFallback)
    {
        const localizedData = {};
        for(const key in this.#value)
        {
            if(key in data)
            {
                localizedData[key] = this.#value[key]._mergeLocalizationIntoData(data[key], locale, localeFallback);
            }
            else
            {
                localizedData[key] = this.#value[key]._toLocalizedJSON(locale);
            }
        }
        return localizedData;
    }

    _markAsLocalizedByData(data)
    {
        for(const key in this.#value)
        {
            if(key in data)
            {
                this.#value[key]._markAsLocalizedByData(data[key]);
            }
        }
    }

    _destroy()
    {
        super._destroy();
        for(let key in this.#value)
        {
            this.#value[key]._destroy();
        }
    }

    _updateStorageData()
    {
        if(!this._hasDraft)
        {
            return;
        }
        this._setDraft();
        if('$draft' in this._revisionDataObject)
        {
            this._revisionDataObject.$draft = this._mergeLocalizationIntoData(this._revisionDataObject.$draft, this.#storage.locale, this.#storage.localeFallback);
        }
        else if('$revisions' in this._revisionDataObject)
        {
            this._revisionDataObject.$draft = this._toLocalizedJSON(this.#storage.locale);
        }
        else
        {
            const draft = this._mergeLocalizationIntoData(this._revisionDataObject, this.#storage.locale, this.#storage.localeFallback);
            xClearObject(this._revisionDataObject);
            xMerge(this._revisionDataObject, draft);
        }

    }
}
export function xObject(value, typename = null)
{
    return new XObject(value, typename);
}

export class XReference extends XType 
{
    #typenames = null;
    #key = '';
    #storage = null;

    constructor(typenames, storage = null)
    {
        super();
        this.#storage = storage;
        this.#typenames = xIsArray(typenames) ? typenames : [typenames];
        for(const typename of this.#typenames)
        {
            if(!(xIsString(typename)))
            {
                throw new XTypeError('If typename is a native JavaScript array, each item needs to be a native JavaScript string!', this, typename);
            }
        }
    }

    clone(storage)
    {
        const reference = new XReference(this.#typenames, storage ?? this.#storage);
        reference.#key = this.#key;
        return reference;
    }

    set _value(key)
    {
        if(!xIsString(key))
        {
            throw new XTypeError('Value (key) needs to be a string!', this, key);
        }
        const oldKey = this.#key;
        this.#key = key;
        this._callAttachments('_setValue', this, oldKey);
        this._childChanged(this, oldKey);
    }

    get _value()
    {
        return this.#key in this.#storage.data ? this.#storage.data[this.#key] : null;
    }

    get _key()
    {
        return this.#key;
    }

    get _typenames()
    {
        return this.#typenames;
    }

    toString()
    {
        return this.#key;
    }

    valueOf()
    {
        return this.#key;
    }

    toJSON()
    {
        return this.#key;
    }
}
export function xReference(typenames)
{
    return new XReference(typenames);
}

class XNamedTypes 
{
    #list  = [];
    #named = {};

    constructor(...types)
    {
        this.addTypes(...types);
    }

    addTypes(...types)
    {
        for(const type of types)
        {
            if(!xIsSpecificClass(type, XObject))
            {
                throw new XTypeError('Types needs to be a native JavaScript array with items of XObject that have a unique typename!', this, type);
            }
            if(xIsNull(type._typename))
            {
                throw new XTypeError('Types needs to be a native JavaScript array with items of XObject that have a unique typename, but this object does not have a typename!', this, type);
            }
            if(type._typename in this.#named)
            {
                throw new XTypeError('Types needs to be a native JavaScript array with items of XObject that have a unique typename, but the typename "' + type._typename + '" is already taken.', this, type);
            }
            this.#list.push(type);
            this.#named[type._typename] = type;
        }
    }

    get list  () { return this.#list;  }
    get named () { return this.#named; }
}

export class XStorage 
{
    #types = {};
    #versions;
    #versionLookupIndex;
    #versionsLocales;
    #versionsName;
    #storageData;
    #reactiveData;
    #reactiveDataLocale = xString();
    #reactiveDataLocaleFallback = xString('en');
    #reactiveDataVersion = xString('*');

    constructor(types=[], versionsAndLocales=[{}])
    {
        this.types = types;
        this.reset(versionsAndLocales);
    }

    reset(versionsAndLocales=[{}])
    {
        this.#destroyReactiveData();
        this.#versions = [];
        this.#versionLookupIndex = {};
        this.#versionsLocales = {};
        this.#versionsName = {};
        this.#storageData = {};
        this.#reactiveDataLocale._value = '';
        this.#reactiveDataLocaleFallback._value = 'en';
        this.#reactiveDataVersion._value = '*';

        if(!xIsArray(versionsAndLocales))
        {
            throw new XTypeError('Version locales needs to be a native JavaScript array of native JavaScript objects that define a version tag and/or locales, e.g. [{version:"0.0.0",locales["en"]},{version:"0.0.1",locales["en","de"]}]!', this, versionsAndLocales);
        }
        for(const versionAndLocales of versionsAndLocales)
        {
            if(!xIsObject(versionAndLocales))
            {
                throw new XTypeError('Version locale items need to be native JavaScript objects that define a version tag and/or locales, e.g. {version:"0.0.1",locales["en","de"]}!', this, versionsAndLocales);
            }
            const version = versionAndLocales.version ?? '*';
            if(version in this.#versionLookupIndex)
            {
                throw new XTypeError('Version "' + version + '" is defined multiple times!', this, versionsAndLocales);
            }
            this.#versionLookupIndex[version] = this.#versions.length;
            this.#versions.push(version);
            const versionLocales = versionAndLocales.locales ?? [];
            for(const versionLocale in versionLocales)
            {
                if(!xIsString(versionLocale))
                {
                    throw new XTypeError('Each defined locale for version "' + version + '" needs to be a native JavaScript string!', this, versionLocale);
                }
            }
            this.#versionsLocales[version] = versionLocales;
            this.#versionsName[version] = versionAndLocales.name ?? versionAndLocales.version;
        }
    }

    #destroyReactiveData()
    {
        if(!this.#reactiveData)
        {
            this.#reactiveData = {};
            return;
        }
        for(const key in this.#reactiveData)
        {
            this.#reactiveData[key]._destroy();
        }
        this.#reactiveData = {};
    }

    get versionsAndLocales()
    {
        const versionsAndLocales = [];
        for(const version of this.#versions)
        {
            const versionAndLocales = {};
            if(version != '*')
            {
                versionAndLocales.version = version;
                versionAndLocales.name = this.#versionsName[version];
            }
            const locales = this.#versionsLocales[version];
            if(locales.length > 0)
            {
                versionAndLocales.locales = locales;
            }
            versionsAndLocales.push(versionAndLocales);
        }
        return versionsAndLocales;
    }

    createView(version, locale)
    {
        const storage = new XStorage();
        storage.#types = this.#types;
        storage.#versions = this.#versions;
        storage.#versionLookupIndex = this.#versionLookupIndex;
        storage.#versionsLocales = this.#versionsLocales;
        storage.#versionsName = this.#versionsName;
        storage.#versions = this.#versions;
        storage.#storageData = this.#storageData;
        storage.setVersionAndLocale(version ?? this.version, locale ?? this.locale);
        return storage;
    }

    #createInfoString(info)
    {
        const infos = [];
        for(const key in info)
        {
            if(xIsUndefined(info[key]))
            {
                continue;
            }
            infos.push(key + ': "' + info[key] + '"');
        }
        return '(' + infos.join(', ') + ')'
    }

    #validateStorageRevisionDataObject(type, key, version, revisionDataObject)
    {
        if('$draft' in revisionDataObject || '$revisions' in revisionDataObject)
        {
            for(let key in revisionDataObject)
            {
                if(key == '$draft' || key == '$revisions')
                {
                    continue;
                }
                throw new XTypeError('If $draft or $revisions is specified, the object may not have any other attributes ' + this.#createInfoString({Key:key, Version:version}) + '!', this, revisionDataObject);
            }
        }
        else
        {
            if(!xIsNull(type))
            {
                type.clone(this)._value = revisionDataObject;
            }
        }
        if('$revisions' in revisionDataObject)
        {
            const revisions = revisionDataObject.$revisions;
            if(!xIsObject(revisions))
            {
                throw new XTypeError('Revisions need to be a native JavaScript object that map timestamps to data objects ' + this.#createInfoString({Key:key, Version:version}) + '!', this, revisions);
            }
            for(let timestampString in revisions)
            {
                const timestamp = parseInt(timestampString);
                if(isNaN(timestamp))
                {
                    throw new XTypeError('Revision keys need to be timestamps ' + this.#createInfoString({Key:key, Version:version}) + '!', this, timestampString);
                }
                const data = revisions[timestampString];
                if(!xIsObject(data))
                {
                    throw new XTypeError('Each revision needs to be a native JavaScript object with the data ' + this.#createInfoString({Key:key, Version:version, Timestamp:timestampString}) + '!', this, data);
                }
                if(!xIsNull(type))
                {
                    type.clone(this)._value = data;
                }
            }
        }
        if('$draft' in revisionDataObject)
        {
            const data = revisionDataObject.$draft;
            if(!xIsObject(data))
            {
                throw new XTypeError('Draft data needs to be a native JavaScript object ' + this.#createInfoString({Key:key, Version:version}) + '!', this, data);
            }
            if(!xIsNull(type))
            {
                type.clone(this)._value = data;
            }
        }
    }

    validateStorageItem(key, withObjects)
    {
        const versionedRevisionDataObject = this.#storageData[key];
        if(!xIsObject(versionedRevisionDataObject))
        {
            if(this.isVersioned)
            {
                throw new XTypeError('Versioned data item format is wrong. Should be something like {"$":"MyType","0.0.0":{"my-field":"my-value",...},...} ' + this.#createInfoString({Key:key}) + '!', this, versionedRevisionDataObject);
            }
            else
            {
                throw new XTypeError('Data item format is wrong. Should be something like {"$":"MyType","my-field":"my-value",...} ' + this.#createInfoString({Key:key}) + '!', this, versionedRevisionDataObject);
            }
        }
        if(!('$' in versionedRevisionDataObject))
        {
            throw new XTypeError('Each data item needs to specify an existing object type (e.g. "$":"MyType"), but your data does not provide one ' + this.#createInfoString({Key:key}) + '!', this, versionedRevisionDataObject);
        }
        if(!(versionedRevisionDataObject.$ in this.#types))
        {
            throw new XTypeError('Each data item needs to specify an existing object type (e.g. "$":"MyType"), but type "' + versionedRevisionDataObject.$ + '" is not specified ' + this.#createInfoString({Key:key}) + '!', this, versionedRevisionDataObject);
        }
        const type = withObjects ? this.#types[versionedRevisionDataObject.$] : null;
        if(this.isVersioned)
        {
            for(const version in versionedRevisionDataObject)
            {
                if(version === '$')
                {
                    continue;
                }
                const revisionDataObject = versionedRevisionDataObject[version];
                if(!(version in this.#versionLookupIndex))
                {
                    throw new XTypeError('Did not find version "' + version + '" in ' + xStringify(this.#versions) + ' ' + this.#createInfoString({Key:key, Version:version}) + '!', this, revisionDataObject);
                }
                if(!xIsObject(revisionDataObject))
                {
                    throw new XTypeError('Each data item needs to be a native JavaScript object ' + this.#createInfoString({Key:key, Version:version}) + '!', this, revisionDataObject);
                }
                this.#validateStorageRevisionDataObject(type, key, version, revisionDataObject);
            }
        }
        else
        {
            this.#validateStorageRevisionDataObject(type, key, undefined, versionedRevisionDataObject);
        }
    }

    validateStorage(withObjects)
    {
        withObjects = !xIsUndefined(withObjects) ? withObjects : false;
        if(!xIsObject(this.#storageData))
        {
            if(this.isVersioned)
            {
                throw new XTypeError('Versioned data format is wrong. Should be something like {"my-key":{"$":"MyType","0.0.0":{"my-field":"my-value",...},...},...}!', this, this.#storageData);
            }
            else
            {
                throw new XTypeError('Data format is wrong. Should be something like {"my-key":{"$":"MyType","my-field":"my-value",...},...}!', this, this.#storageData);
            }
        }
        for(const key in this.#storageData)
        {
            this.validateStorageItem(key, withObjects);
        }
    }

    set storage(data)
    {
        this.#destroyReactiveData();
        this.#storageData = data;
        this.validateStorage();
        if(!this.isVersioned && !this.isLocalized)
        {
            this.#createReactiveData();
        }
    }

    merge(data)
    {
        this.#destroyReactiveData();
        xDeepMerge(this.#storageData, data);
        this.validateStorage();
        if(!this.isVersioned && !this.isLocalized)
        {
            this.#createReactiveData();
        }
    }

    get storage()
    {
        return this.#storageData;
    }

    updateStorageDataFromReactiveData()
    {
        for(const key in this.#reactiveData)
        {
            this.#reactiveData[key]._updateStorageData();
        }
    }

    toJSON()
    {
        const data = {};
        for(const key in this.#reactiveData)
        {
            data[key] = this.#reactiveData[key].toJSON();
        }
        return data;
    }

    _toLocalizedJSON()
    {
        const data = {};
        for(const key in this.#reactiveData)
        {
            data[key] = this.#reactiveData[key]._toLocalizedJSON();
        }
        return data;
    }

    _extractLocalization(locale, localeFallback, version, keepStructure=true)
    {
        locale = locale ?? this.locale;
        localeFallback = localeFallback ?? this.localeFallback;
        version = version ?? this.version;
        if(!xIsString(version))
        {
            throw new XTypeError('Version needs to be a native JavaScript string.', this, version);
        }
        if(!this.isVersioned && version != '*')
        {
            throw new XTypeError('The data is not versioned, so version has to be "*".', this);
        }
        if(this.isVersioned && version == '*')
        {
            version = this.#versions[this.#versions.length-1];
        }
        if(!(version in this.#versionLookupIndex))
        {
            throw new XTypeError('Version is not defined in storage.', this, version, this.#versions);
        }
        const storage = {};
        for(const key in this.#storageData)
        {
            const versionedRevisionDataObject = this.#storageData[key];
            const currentVersionedRevisionDataObject = {
                $: versionedRevisionDataObject.$
            };
            const reactiveData = this.#types[versionedRevisionDataObject.$].clone(this);
            for(let i = this.#versionLookupIndex[version]; i >= 0; i--)
            {
                const version = this.#versions[i];
                let revisionDataObject = versionedRevisionDataObject;
                let currentRevisionDataObject = currentVersionedRevisionDataObject;
                if(version != '*')
                {
                    if(!(version in versionedRevisionDataObject))
                    {
                        continue;
                    }
                    revisionDataObject = revisionDataObject[version];
                    currentVersionedRevisionDataObject[version] = {};
                    currentRevisionDataObject = currentVersionedRevisionDataObject[version];
                }
                const hasDraft = '$draft' in revisionDataObject;
                const hasRevisions = '$revisions' in revisionDataObject;
                if(!hasDraft && !hasRevisions)
                {
                    revisionDataObject = reactiveData._extractLocalizationFromData(revisionDataObject, locale, localeFallback, keepStructure);
                    xMerge(currentRevisionDataObject, revisionDataObject);
                }
                if(hasDraft)
                {
                    currentRevisionDataObject.$draft = reactiveData._extractLocalizationFromData(revisionDataObject.$draft, locale, localeFallback, keepStructure)
                }
                if(hasRevisions)
                {
                    currentRevisionDataObject.$revisions = {};
                    for(const revision in revisionDataObject.$revisions)
                    {
                        const dataObject = revisionDataObject[revision];
                        currentRevisionDataObject.$revisions[revision] = reactiveData._extractLocalizationFromData(revisionDataObject.$revisions[revision], locale, localeFallback, keepStructure)
                    }
                }
            }
            storage[key] = currentVersionedRevisionDataObject;
        }
        return storage;
    }

    _accessLatestDraftLocalizations()
    {
        const localizedData = [];
        for(const key in this.#reactiveData)
        {
            const reactiveData = this.#reactiveData[key];
            const revisionDataObject = reactiveData._revisionDataObject;
            let dataObject = null;
            if('$draft' in revisionDataObject)
            {
                dataObject = revisionDataObject.$draft;
            }
            else if(!('$revisions' in revisionDataObject))
            {
                dataObject = revisionDataObject;
            }
            if(dataObject)
            {
                localizedData.push(...reactiveData._accessLocalizationsInData(dataObject, [reactiveData._typename]));
            }
        }
        return localizedData;
    }

    #createReactiveDataItem(key, version, locale)
    {
        const versionedRevisionDataObject = this.#storageData[key];
        let revisionDataObject = versionedRevisionDataObject;
        let foundVersion = false;
        if(this.isVersioned)
        {
            for(let i = this.#versionLookupIndex[version]; i >= 0; i--)
            {
                foundVersion = this.#versions[i];
                if(foundVersion in revisionDataObject)
                {
                    revisionDataObject = revisionDataObject[foundVersion];
                    break;
                }
            }
            if(foundVersion === false)
            {
                if(key in this.#reactiveData)
                {
                    this.#reactiveData[key]._destroy();
                    delete this.#reactiveData[key];
                }
                return;
            }
        }
        const reactiveData = key in this.#reactiveData ? this.#reactiveData[key] : this.#types[versionedRevisionDataObject.$].clone(this);
        let dataObject = revisionDataObject;
        if('$revisions' in revisionDataObject)
        {
            reactiveData._revisions = revisionDataObject.$revisions;
            reactiveData._revisionTimestamps = [];
            for(let timestampString in reactiveData._revisions)
            {
                reactiveData._revisionTimestamps.push(parseInt(timestampString));
            }
            reactiveData._currentRevision = -1;
            reactiveData._revisionTimestamps = reactiveData._revisionTimestamps.sort((a,b)=>a-b);
        }
        if('$draft' in revisionDataObject)
        {
            dataObject = revisionDataObject.$draft;
        }
        else if('$revisions' in revisionDataObject && reactiveData._revisionTimestamps.length > 0)
        {
            reactiveData._currentRevision = reactiveData._revisionTimestamps[reactiveData._revisionTimestamps.length-1];
            dataObject = reactiveData._revisions[String(reactiveData._currentRevision)];
        }
        reactiveData._key = key;
        if(this.isVersioned)
        {
            reactiveData._version = foundVersion;
        }
        reactiveData._revisionDataObject = revisionDataObject;
        reactiveData._value = locale ? reactiveData._extractLocalizationFromData(dataObject, locale, this.localeFallback) : dataObject;
        reactiveData._markAsLocalizedByData(dataObject);
        this.#reactiveData[key] = reactiveData;
        return reactiveData;
    }

    #createReactiveData(version='*', locale='')
    {
        for(const key in this.#storageData)
        {
            this.#createReactiveDataItem(key, version, locale)
        }
    }

    setVersionAndLocale(version='*', locale='')
    {
        version = version ?? '*';
        if(!xIsString(version))
        {
            throw new XTypeError('Version needs to be a native JavaScript string.', this, version);
        }
        if(!this.isVersioned && version != '*')
        {
            throw new XTypeError('The data is not versioned, so version has to be "*".', this);
        }
        if(!(version in this.#versionLookupIndex))
        {
            throw new XTypeError('Version is not defined in storage.', this, version, this.#versions);
        }
        const versionLocales = this.#versionsLocales[version];
        if(locale)
        {
            if(!xIsString(locale))
            {
                throw new XTypeError('If set, locale needs to be a native JavaScript string.', this, locale);
            }
            if(!versionLocales.includes(locale))
            {
                throw new XTypeError('Version "' + version + '" does define locale "' + locale + '".', this);
            }
        }
        else
        {
            if(versionLocales.length > 0)
            {
                throw new XTypeError('Version "' + version + '" defines locales, so one has to be chosen.', this, versionLocales);
            }
            locale = '';
        }
        this.updateStorageDataFromReactiveData();
        this.#createReactiveData(version, locale);
        this.#reactiveDataLocale._value = locale;
        this.#reactiveDataVersion._value = version;
    }

    set version(version)
    {
        this.setVersionAndLocale(version, this.locale);
    }

    get version()
    {
        return this.#reactiveDataVersion._value;
    }

    get version_()
    {
        return this.#reactiveDataVersion;
    }

    get data()
    {
        return this.#reactiveData;
    }

    set locale(locale)
    {
        this.setVersionAndLocale(this.version, locale);
    }

    get locale()
    {
        return this.#reactiveDataLocale._value;
    }

    get locale_()
    {
        return this.#reactiveDataLocale;
    }

    set localeFallback(locale)
    {
        this.#reactiveDataLocaleFallback._value = locale;
    }

    get localeFallback()
    {
        return this.#reactiveDataLocaleFallback._value;
    }

    get localeFallback_()
    {
        return this.#reactiveDataLocaleFallback;
    }

    get localeOrFallback()
    {
        return this.locale ? this.locale : this.localeFallback;
    }

    addTypes(...types)
    {
        for(const type of types)
        {
            if(!xIsSpecificClass(type, XObject))
            {
                throw new XTypeError('Types needs to be a native JavaScript array with items of XObject that have a unique typename!', this, type);
            }
            if(xIsNull(type._typename))
            {
                throw new XTypeError('Types needs to be a native JavaScript array with items of XObject that have a unique typename, but this object does not have a typename!', this, type);
            }
            if(type._typename in this.#types)
            {
                throw new XTypeError('Types needs to be a native JavaScript array with items of XObject that have a unique typename, but the typename "' + type._typename + '" is already taken.', this, type);
            }
            this.#types[type._typename] = type;
        }
    }

    set types(types)
    {
        this.#types = {};
        this.addTypes(...types);
    }

    get types()
    {
        return this.#types;
    }

    get isVersioned()
    {
        return this.#versions.length > 1 || this.#versions[0] != '*';
    }

    get isLocalized()
    {
        return this.#versionsLocales[this.version].length > 0;
    }

    get versions()
    {
        return this.#versions;
    }

    get locales()
    {
        return this.#versionsLocales[this.version];
    }

    get versionName()
    {
        return this.#versionsName[this.version];
    }

    addVersion(name)
    {
        const version = this.generateKey();
        if(this.#versions.includes(version))
        {
            return false;
        }
        this.#versionLookupIndex[version] = this.#versions.length;
        this.#versions.push(version);
        this.#versionsLocales[version] = [];
        this.#versionsName[version] = name ?? version;
        return version;
    }

    addLocale(locale)
    {
        if(!xIsString(locale))
        {
            throw new XTypeError('Locale needs to be a native JavaScript string!', this, locale);
        }
        const versionLocales = this.#versionsLocales[this.version];
        versionLocales.push(locale);
    }

    generateKey()
    {
        for(let i = 0; i < 1000; i++)
        {
            const key = xRandomString(12);
            if(!(key in this.#storageData))
            {
                return key;
            }
        }
        return 'THIS@SHOULD@NEVER@HAPPEN';
    }

    addData(typename, version)
    {
        version = version ?? this.version;
        if(!(typename in this.#types))
        {
            throw new XTypeError('Typename does not exist.', this, typename);
        }
        const type = this.#types[typename];
        let data = type.clone(this).toJSON();
        if(this.isVersioned)
        {
            delete data.$;
            data = {
                $: typename,
                [version]: data
            }
        }
        const key = this.generateKey();
        this.#storageData[key] = data;
        return this.#createReactiveDataItem(key, version, this.locale);
    }
}

export class XAttachmentHandler
{
    #attachments = new Map();

    add(data, attachment, callSetValueOrChanged = true)
    {
        data = xIsArray(data) ? data : [data];
        for(const dataItem of data)
        {
            if(!this.#attachments.has(dataItem))
            {
                this.#attachments.set(dataItem, new Set());
            }
            if(!this.#attachments.get(dataItem).has(attachment))
            {
                this.#attachments.get(dataItem).add(attachment);
                dataItem._addAttachment(attachment);
            }
        }
        if(callSetValueOrChanged)
        {
            if('_setValue' in attachment)
            {
                attachment._setValue(data[0]);
            }
            else if('_changed' in attachment)
            {
                attachment._changed(data[0]);
            }
        }
    }

    remove(data, attachment)
    {
        data = xIsArray(data) ? data : [data];
        for(const dataItem of data)
        {
            if(this.#attachments.has(dataItem))
            {
                const set = this.#attachments.get(dataItem);
                if(set.has(attachment))
                {
                    set.delete(attachment);
                    if(set.size == 0)
                    {
                        this.#attachments.delete(dataItem);
                    }
                    dataItem._removeAttachment(attachment);
                }
            }
        }
    }

    destroy()
    {
        for(const [data, set] of this.#attachments)
        {
            for(const attachment of set)
            {
                data._removeAttachment(attachment);
            }
            set.clear();
        }
        this.#attachments.clear();
    }
}

export function xComputedOutput(attachmentHandler, output, compute, ...parameters)
{
    const attachment = {
        _changed()
        {
            output._value = compute(...parameters);
        }
    }
    for(const parameter of parameters)
    {
        if(parameter instanceof XType)
        {
            attachmentHandler.add(parameter, attachment, false);
        }
    }
    attachment._changed();
    return output;
}

export function xComputed(attachmentHandler, compute, ...parameters)
{
    const attachment = {
        _changed()
        {
            compute(...parameters);
        }
    }
    for(const parameter of parameters)
    {
        if(parameter instanceof XType)
        {
            attachmentHandler.add(parameter, attachment, false);
        }
    }
    attachment._changed();
}

export class XAttachmentArray extends Array
{
    #dataArray;
    #dataClass;
    #parameters;

    constructor(dataArray, dataClass, ...parameters)
    {
        super();
        this.#dataArray = dataArray;
        this.#dataClass = dataClass;
        this.#parameters = parameters;
        this.#dataArray._addAttachment(this);
        this._setValue(dataArray);
    }

    _destroy()
    {
        if('_destroy' in this.#dataClass.prototype)
        {
            for(const item of this)
            {
                item._destroy();
            }
        }
        this.#dataArray._removeAttachment(this);
    }

    _setValue(dataArray)
    {
        this.length = 0;
        for(let i = 0; i < dataArray.length; i++)
        {
            this.push(dataArray[i]);
        }
    }

    push(...values)
    {
        for(const value of values)
        {
            super.push(new this.#dataClass(value, ...this.#parameters));
        }
        return this.length;
    }

    pop()
    {
        if(this.length > 0)
        {
            this[this.length-1]._destroy();
        }
        super.pop();
    }

    splice(start, deleteCount, ...values)
    {
        return;
        deleteCount = Math.max(deleteCount, 0);
        start = Math.min(this.length, Math.max(-this.length, start));
        if(start < 0)
        {
            start = this.length + start;
        }
        const end = Math.min(start + deleteCount, this.length);
        for(let i = start; i < end; i++)
        {
            this[i]._destroy();
        }
        const newItems = [];
        for(const value of values)
        {
            newItems.push(new this.#dataClass(value, ...this.#parameters));
        }
        return super.splice(start, deleteCount, ...newItems);
    }
}
